import { SmartformFieldData, SmartformElementData, Tuple } from '../../types';
import { getModelPathFromIndex } from './model/getModelPath';
import { objectUtil } from './objectUtil';
import { pointInTimeToDate } from './dateHelpers';
import { InputModel } from './model';

interface ReturnType<Model, NumRequired extends number = 1> {
  required: Tuple<SmartformFieldData<Model>, NumRequired>;
  optional: { [prop: string]: SmartformFieldData<Model> };
}

/**
 * Gets all required and optional fields of a given element
 *
 * creates new FormControlProps for each of the fields of the element
 *
 * makes the output more friendly
 */
export function getElementFields<N extends number = 1, Model = InputModel>(elementData: SmartformElementData<Model>) {
  const { element, touched, errors, submitted, hideEmptyHints } = elementData;

  const required = [] as Tuple<SmartformFieldData<Model>, N>;
  const optional: { [prop: string]: SmartformFieldData<Model> } = {};

  element.fields.forEach((field, index) => {
    const name = getModelPathFromIndex(element, index);
    const val = field.validation;
    const resultField: SmartformFieldData<Model> = {
      name,
      meta: field.fieldName ? element.meta.find((meta) => meta.fieldName === field.fieldName) : element.meta[index],
      initialValue: field.initialValue ?? '',
      hint: field.hint ?? '',
      helpText: field.helpText ?? (hideEmptyHints ? '' : ' '),
      infoText: field.info ?? '',
      label: field.label ?? '',
      validation: field.validation
        ? {
            required: !!val?.required,
            email: !!val?.email,
            min: val?.min,
            minDate: val?.min?.[0] ? pointInTimeToDate(val.min?.[0], val.min?.[2], true) : undefined,
            minDateMessage: val?.min?.[1],
            max: val?.max,
            maxDate: val?.max?.[0] ? pointInTimeToDate(val.max?.[0], val.max?.[2], false) : undefined,
            maxDateMessage: val?.max?.[1],
            matches: val?.matches?.[0],
            allowedCountryCodes: val?.allowedCountryCodes
          }
        : {},
      error: submitted ? (objectUtil.getIn(errors, name) ?? undefined) : undefined, // because validateOnMount is set by default - before passing errors to components we need to check that form is submitted
      touched: submitted || objectUtil.getIn(touched, name),
      readonly: !!field.readonly,
      excludedValues: field.excludedValues,
      allowedValues: field.allowedValues,
      readOnlyValues: field.readOnlyValues,
      requiredValues: field.requiredValues,
      currency: field.currency,
      sort: field.sort
    };
    if (field.fieldName) {
      optional[field.fieldName] = resultField;
    } else {
      required.push(resultField);
    }
  });

  return {
    required: required as unknown as Tuple<SmartformFieldData<Model>, N>,
    optional
  } as ReturnType<Model, N>;
}
